import React, {useState} from 'react';
import {Box, Button, Grid, IconButton, Menu, MenuItem} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {Option, PictureChoiceQuestion} from 'crqlar-react-common/dist/models/forms/form';
import EditableTypography from 'crqlar-react-common/dist/components/EditableTypography';
import MdEditor from 'crqlar-react-common/dist/components/MdEditor';
import ImagePicker from "crqlar-react-common/dist/components/ImagePicker";
import {useTranslation} from 'react-i18next';

interface PictureChoiceEditorProps {
    question: PictureChoiceQuestion;
    onQuestionUpdate: (question: PictureChoiceQuestion) => void;
}

const PictureChoiceEditor: React.FC<PictureChoiceEditorProps> = ({
                                                                     question,
                                                                     onQuestionUpdate
                                                                 }) => {
    const {t} = useTranslation();
    const [optionMenuAnchorEl, setOptionMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
    const [editingImageOptionId, setEditingImageOptionId] = useState<string | null>(null);

    // Generate a unique ID for new options
    const generateUniqueId = () => Math.random().toString(36).substring(2, 15);

    // Handle title update
    const handleTitleUpdate = (value: string) => {
        onQuestionUpdate({
            ...question,
            title: value,
        });
    };

    // Handle description update
    const handleDescriptionUpdate = (description?: string) => {
        onQuestionUpdate({
            ...question,
            description: description || ''
        });
    };

    // Handle option text update
    const handleOptionUpdate = (optionId: string, text: string) => {
        const updatedOptions = question.options?.map(opt =>
            opt.id === optionId ? {...opt, label: text} : opt
        );

        onQuestionUpdate({
            ...question,
            options: updatedOptions,
        });
    };

    // Handle add new option
    const handleAddOption = () => {
        const newOption: Option = {
            id: generateUniqueId(),
            label: t('Option {{number}}', {number: (question.options?.length || 0) + 1})
        };

        onQuestionUpdate({
            ...question,
            options: [...(question.options || []), newOption],
        });
    };

    // Handle delete option
    const handleDeleteOption = (optionId: string) => {
        onQuestionUpdate({
            ...question,
            options: question.options?.filter(opt => opt.id !== optionId),
        });
        handleOptionMenuClose();
    };

    // Handle opening option menu
    const handleOptionMenuOpen = (event: React.MouseEvent<HTMLElement>, optionId: string) => {
        event.stopPropagation();
        setOptionMenuAnchorEl(event.currentTarget);
        setSelectedOptionId(optionId);
    };

    // Handle closing option menu
    const handleOptionMenuClose = () => {
        setOptionMenuAnchorEl(null);
        setSelectedOptionId(null);
    };

    // Toggle allow multiple selection
    const handleToggleMultiple = () => {
        onQuestionUpdate({
            ...question,
            allowMultiple: !question.allowMultiple,
        });
        handleOptionMenuClose();
    };

    // Handle option image change
    const handleOptionImageChange = (optionId: string, media: any) => {
        const updatedOptions = question.options?.map(option => {
            if (option.id === optionId) {
                return {
                    ...option,
                    imageMedia: media
                };
            }
            return option;
        });
        onQuestionUpdate({
            ...question,
            options: updatedOptions,
        });
        // Close the dialog after selecting/removing image
        setEditingImageOptionId(null);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                p: 2,
            }}
        >
            {/* Question Title */}
            <EditableTypography
                value={question.title}
                onChange={handleTitleUpdate}
                placeholder={t('Your question here')}
                variant="h6"
                multiline
                sx={{
                    mb: 2,
                    width: '100%',
                    fontWeight: 'medium',
                    textAlign: 'left',
                    padding: '8px',
                    borderRadius: 1,
                }}
            />

            {/* Description */}
            <Box sx={{mb: 3, width: '100%'}}>
                <MdEditor
                    value={question.description || ''}
                    onChange={handleDescriptionUpdate}
                    placeholder={t('Question description (optional)')}
                    className={'mini-mdx'}
                />
            </Box>

            {/* Multiple Selection Toggle */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                        opacity: 0.8,
                    }
                }}
                onClick={handleToggleMultiple}
            >
                {question.allowMultiple ? (
                    <CheckBoxIcon sx={{mr: 1, fontSize: 20}}/>
                ) : (
                    <CheckBoxOutlineBlankIcon sx={{mr: 1, fontSize: 20}}/>
                )}
                <Box component="span" sx={{fontSize: '0.875rem'}}>
                    {t('Allow multiple selections')}
                </Box>
            </Box>

            {/* Options Grid */}
            <Grid container spacing={2} sx={{mb: 2}}>
                {question.options?.map((option) => (
                    <Grid xs={6} key={option.id}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                p: 1,
                                bgcolor: 'grey.50',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                position: 'relative',
                                '&:hover': {
                                    bgcolor: 'grey.100',
                                },
                            }}
                        >
                            {/* Image Placeholder */}
                            <Box
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingImageOptionId(option.id);
                                }}
                                sx={{
                                    height: 120,
                                    bgcolor: 'grey.200',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 1,
                                    mb: 1,
                                    cursor: 'pointer',
                                    position: 'relative'
                                }}
                            >
                                <ImagePicker
                                    image={option.imageMedia}
                                    setImage={image => handleOptionImageChange(option.id, image)}
                                    height={120}
                                    width={'100%'}
                                />
                            </Box>

                            {/* Option Text */}
                            <EditableTypography
                                value={option.label}
                                onChange={value => handleOptionUpdate(option.id, value)}
                                sx={{
                                    padding: '4px',
                                    borderRadius: 1,
                                    textAlign: 'left',
                                    color: 'text.secondary',
                                }}
                                placeholder={t('Option text')}
                            />

                            {/* Delete Button */}
                            <IconButton
                                size="small"
                                onClick={(e) => handleOptionMenuOpen(e, option.id)}
                                sx={{
                                    position: 'absolute',
                                    top: 5,
                                    right: 5,
                                    bgcolor: 'background.paper',
                                    '&:hover': {
                                        bgcolor: 'grey.200',
                                    },
                                }}
                            >
                                <DeleteIcon fontSize="small"/>
                            </IconButton>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {/* Add Option Button */}
            <Button
                startIcon={<AddIcon/>}
                onClick={handleAddOption}
                variant="outlined"
                sx={{
                    borderStyle: 'dashed',
                    textTransform: 'none',
                    borderRadius: 1,
                    py: 1,
                    width: '100%',
                    justifyContent: 'flex-start',
                }}
            >
                {t('Add choice')}
            </Button>

            {/* Option Context Menu */}
            <Menu
                anchorEl={optionMenuAnchorEl}
                open={Boolean(optionMenuAnchorEl)}
                onClose={handleOptionMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem
                    onClick={() => {
                        if (selectedOptionId) handleDeleteOption(selectedOptionId);
                    }}
                    dense
                >
                    <DeleteIcon fontSize="small" sx={{mr: 1}}/>
                    {t('Delete')}
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default PictureChoiceEditor;
